import { getDownloadURL, getStorage, ref, uploadBytes, deleteObject } from 'firebase/storage'
import { app } from '@/shared/firebase/client'

/**
 * Storing a profile photo.
 *
 * Cropping and encoding happen in `PhotoEditor`; this uploads what it produces.
 * The bytes never arrive at full size — a 12-megapixel phone photo would be
 * four megabytes for something displayed at 84 pixels, stored forever and paid
 * for in egress every time a colleague's face renders beside a lead.
 *
 * The path carries no file extension, so replacing a photo overwrites the old
 * one rather than leaving it orphaned in the bucket and still readable.
 */

/**
 * What the picker offers.
 *
 * `image/*` rather than a list, because the editor re-encodes everything to
 * JPEG anyway — and an iPhone hands over HEIC, which an explicit list would
 * refuse for no reason. It is also what makes iOS and Android offer the camera
 * alongside the photo library.
 */
export const ACCEPTED_IMAGE_TYPES = 'image/*'

function pathFor(email: string): string {
  return `profilePhotos/${email.trim().toLowerCase()}`
}

/** Upload the cropped image and return the URL to store on the user record. */
export async function uploadProfilePhoto(email: string, blob: Blob): Promise<string> {
  const target = ref(getStorage(app), pathFor(email))
  await uploadBytes(target, blob, {
    contentType: 'image/jpeg',
    // A photo changes rarely and is shown constantly. A year of caching, with
    // the download URL's own token changing whenever the object is replaced —
    // so a new photo appears immediately rather than after the cache expires.
    cacheControl: 'public, max-age=31536000',
  })
  return getDownloadURL(target)
}

/** Remove it. Succeeds quietly when there was nothing there. */
export async function removeProfilePhoto(email: string): Promise<void> {
  try {
    await deleteObject(ref(getStorage(app), pathFor(email)))
  } catch (e) {
    // object-not-found means the goal is already met. Anything else is real.
    if ((e as { code?: string }).code !== 'storage/object-not-found') throw e
  }
}
